import { useState, useEffect } from 'react';
import Router, { withRouter } from 'next/router';
import Cookies from 'js-cookie';
import Select from 'react-select';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';

import { cityList, llc } from '../../constants/cities';
import { SelectStyles } from '../../constants/helpers';
import validationSchema from './ValidationSchema';

import s from './styles.scss';
import { saveFormData } from '../../services/saveFormData';

function debounce(fn, ms) {
  let timer;
  return _ => {
    clearTimeout(timer);
    timer = setTimeout(_ => {
      timer = null;
      fn.apply(this, arguments);
    }, ms);
  };
}

const MainBanner = ({
  above_entity_type,
  cta_button_text,
  hero_title,
  hero_description,
  initialDimensions,
  nextPage,
  pay_title,
  pay_small,
}) => {
  const city_ = Cookies.get('city');
  const llc_ = Cookies.get('llc');
  const defaultCity = (city_ && { value: city_, label: city_ }) || '';
  const defaultLlc = (llc_ && { value: llc_, label: llc_ }) || '';
  const [state, setState] = useState();
  const [entityType, setEntityType] = useState();
  const [dimensions, setDimensions] = useState({
    height: initialDimensions.height,
    width: initialDimensions.width,
  });

  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    }, 500);

    window.addEventListener('resize', debouncedHandleResize);
    return _ => window.removeEventListener('resize', debouncedHandleResize);
  });

  const formik = useFormik({
    initialValues: {
      city: defaultCity,
      llcValue: defaultLlc,
    },
    validationSchema,
    onSubmit: async () => {
      if (formik.isValid) {
        saveFormData('main-page', { state, entityType });
        Router.push(nextPage);
      }
    },
  });

  // ///////////// //
  // Mobile Header
  // ///////////// //
  if (dimensions.width < 1100) {
    return (
      <div className={s.container}>
        <div className={s.content}>
          <div className={s.box}>
            <img src="/static/logo.svg" alt="logo" className={s.logo} />
            <h1>{hero_title}</h1>
            <div className={s.mobileBg}>
              <div className={s.payTitle}>{pay_title}</div>
              <div className={s.smallPay}>{pay_small}</div>
            </div>
          </div>
          <div className={s.mobileBanner}>
            <p className={s.description}>{hero_description}</p>
            <p className={s.entityType}>{above_entity_type}</p>
            <form
              className={s.formSelect}
              onSubmit={formik.handleSubmit}
              id="startForm"
            >
              <div className={s.selectWrap}>
                <div className={s.selectBox}>
                  <Select
                    className={classNames(
                      s.citySelect,
                      formik.touched.city && formik.errors.city && s.redBorder
                    )}
                    styles={SelectStyles}
                    id="city"
                    value={formik.values.city}
                    onChange={e => {
                      Cookies.set('city', e.value);
                      formik.setFieldValue('city', e);
                    }}
                    options={cityList}
                    placeholder="State of Formation"
                  />
                  <span>{formik.touched.city && formik.errors.city}</span>
                </div>
                <div className={s.selectBox}>
                  <Select
                    id="llc"
                    className={classNames(
                      s.citySelect,
                      formik.touched.llcValue &&
                        formik.errors.llcValue &&
                        s.redBorder
                    )}
                    styles={SelectStyles}
                    placeholder="Entity Type"
                    value={formik.values.llcValue}
                    onChange={e => {
                      Cookies.set('llc', e.value);
                      formik.setFieldValue('llcValue', e);
                    }}
                    options={llc}
                  />
                  <span>
                    {formik.touched.llcValue && formik.errors.llcValue}
                  </span>
                </div>
              </div>
              <button form="startForm" type="submit" className={s.button}>
                {cta_button_text}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ////////////// //
  // Desktop Header //
  // ////////////// //
  return (
    <div className={s.container}>
      <div className={s.content}>
        <div className={s.box}>
          <img src="/static/logo.svg" alt="logo" className={s.logo} />
          <h1>{hero_title}</h1>
          <p className={s.description}>{hero_description}</p>
          <div className={s.mobileBg}>
            <div className={s.payTitle}>{pay_title}</div>
            <div className={s.smallPay}>{pay_small}</div>
          </div>
          <form
            className={s.formSelect}
            onSubmit={formik.handleSubmit}
            id="startForm"
          >
            <p className={s.entityType}>{above_entity_type}</p>
            <div className={s.selectWrap}>
              <div className={s.selectBox}>
                <Select
                  className={classNames(
                    s.citySelect,
                    formik.touched.city && formik.errors.city && s.redBorder
                  )}
                  styles={SelectStyles}
                  id="city"
                  value={formik.values.city}
                  onChange={e => {
                    Cookies.set('city', e.value);
                    formik.setFieldValue('city', e);
                  }}
                  options={cityList}
                  placeholder="State of Formation"
                />
                <span>{formik.touched.city && formik.errors.city}</span>
              </div>

              <div className={s.selectBox}>
                <Select
                  id="llc"
                  className={classNames(
                    s.citySelect,
                    formik.touched.llcValue &&
                      formik.errors.llcValue &&
                      s.redBorder
                  )}
                  styles={SelectStyles}
                  placeholder="Entity Type"
                  value={formik.values.llcValue}
                  onChange={e => {
                    Cookies.set('llc', e.value);
                    formik.setFieldValue('llcValue', e);
                  }}
                  options={llc}
                />
                <span>{formik.touched.llcValue && formik.errors.llcValue}</span>
              </div>
            </div>

            <button type="submit" className={s.button} form="startForm">
              {cta_button_text}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

MainBanner.propTypes = {
  above_entity_type: PropTypes.string,
  cta_button_text: PropTypes.string,
  hero_title: PropTypes.string,
  hero_description: PropTypes.string,
  initialDimensions: PropTypes.object,
  pay_title: PropTypes.string,
  pay_small: PropTypes.string,
  nextPage: PropTypes.string,
};

export default withRouter(MainBanner);
